import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Theme, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {connect, useDispatch, useSelector} from 'react-redux';
import { fetchContent } from 'redux/actions/contentful';
import { DocumentBreakpointWrapper } from '../../../components/DocumentModal/DocumentBreakpointWrapper';
import DocumentModal from '../../../components/DocumentModal/DocumentModal';
import { FormAnnotation } from '../../../components/FormAnnotation/FormAnnotation';
import NavigationBar from '../../../components/NavigationBar';
import { PageTitle } from '../../../components/PageTitle/PageTitle';
import { useUniversalTracking } from '../../../lib/tracking/universalTracking';
import { colors } from '../../../theme/theme';
import { AppState } from '../../../types/redux';
import { ContentfulState } from '../../../redux/reducers/contentful/ContentfulState';
import AgreementContainer from './components/AgreementContainer/AgreementContainer';
import {ThunkDispatch} from "redux-thunk";
import {Action} from "redux";
import {finishByEmailProzess} from "../../../redux/actions/contractFinishByEmail";

const useStyles = makeStyles((theme: Theme) => ({
	agreements: {
		backgroundColor: colors.white,
		padding: theme.spacing(3),
	},
	agreementLink: {
		color: theme.palette.primary.main,
		textDecoration: 'underline',
		marginBottom: 16,
		cursor: 'pointer',
	},
	hr: {
		border: `1px solid ${colors.grayA10}`,
		marginBottom: theme.spacing(2),
	},
	errorMessage: {
		color: colors.red,
		fontSize: 14,
		marginTop: 2,
	},
}));

interface Props extends DispatchProps {
	onSubmit(data: any, goToEnd?: boolean): void;
}

const AgreementKeh: FunctionComponent<Props> = (props) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { trackEvent } = useUniversalTracking();

	useEffect(() => {
		dispatch(fetchContent());
	}, [dispatch]);

	const contentful = useSelector<AppState, ContentfulState>(
		(state) => state.contentful
	);

	// base
	const agreementTitle1 = t('agreement.title_1');
	const agreementContent1 = contentful.agreementKeh1;

	const agreementTitle2 = t('agreement.title_2');
	const agreementContent2 = contentful.agreement2;

	const ipidMobile = contentful.IPID_keh_mobile;
	const ipidDesktop = contentful.IPID_keh?.fields?.file?.url;

	const agbMobile = contentful.AGB_keh_mobile;
	const agbDesktop = contentful.AGB_keh?.fields?.file?.url;

	const postboxMobileDoc = contentful.AGB_postbox_mobile
		? documentToReactComponents(contentful.AGB_postbox_mobile)
		: null;
	const postboxDesktopDoc = contentful.AGB_postbox
		? contentful.AGB_postbox.fields.file.url
		: null;

	// form state
	const [checkedCheckbox1, setCheckedCheckbox1] = useState(false);
	const [checkedCheckbox2, setCheckedCheckbox2] = useState(false);

	const [error1, setError1] = useState('');
	const [error2, setError2] = useState('');

	const [showIPIDModal, setShowIPIDModal] = useState(false);
	const [showAllgemeineModal, setShowAllgemeineModal] = useState(false);
	const [showPostboxModal, setShowPostboxModal] = useState(false);

	const [sendEmailErrorMessage, setSendEmailErrorMessage] = useState<string | null>(null);

	const setAgreementErrors = () => {
		setError1(checkedCheckbox1 ? '' : t('agreement.error'));
		setError2(checkedCheckbox2 ? '' : t('agreement.error'));
	};

	const validateAndSubmit = () => {
		if (checkedCheckbox1 && checkedCheckbox2) {
			onSubmit();
		} else {
			setAgreementErrors();
		}
	};

	const onSubmit = () => {
		trackEvent({
			category: 'agreement-submit',
			action: 'click-event',
			label: 'agreement',
		});
		return props
			.finalizeByEmail()
			.then(() => {
				props.onSubmit({}, true);
			})
			.catch((errorMsg) => {
				setSendEmailErrorMessage(errorMsg);
			});
	};

	const getAgreement1ModalLinks = () => (
		<>
			<div
				className={classes.agreementLink}
				onClick={() => {
					trackEvent({
						category: 'agreement-open-document',
						action: 'click-event',
						label: 'agreement IPID',
					});
					setShowIPIDModal(true);
				}}
			>
				{t('agreement.ipid')}
			</div>
			<div
				className={classes.agreementLink}
				onClick={() => {
					trackEvent({
						category: 'agreement-open-document',
						action: 'click-event',
						label: 'agreement Allgemeine Vertragsinformationen',
					});
					setShowAllgemeineModal(true);
				}}
			>
				{t('agreement.allgemeine')}
			</div>
		</>
	);

	const getAgreement2ModalLinks = () => (
		<div
			className={classes.agreementLink}
			onClick={() => {
				trackEvent({
					category: 'agreement-open-document',
					action: 'click-event',
					label: 'agreement Digitales Postfach',
				});
				setShowPostboxModal(true);
			}}
		>
			{t('agreement.postbox')}
		</div>
	);

	return (
		<>
			<PageTitle title={t('agreement.title')} />
			<FormAnnotation>
				<Typography variant="body1">{t('agreement.infobox')}</Typography>
			</FormAnnotation>
			{sendEmailErrorMessage && (
				<div className={classes.errorMessage}>
					{sendEmailErrorMessage}
				</div>
			)}
			<div className={classes.agreements}>
				<AgreementContainer
					data-cy="agreement-check-common"
					title={agreementTitle1}
					content={agreementContent1}
					nonContenfulExtraContent={getAgreement1ModalLinks()}
					error={error1}
					buttonMessage={t('kundendaten.keep_reading')}
					checkedCheckbox={checkedCheckbox1}
					setCheckedCheckbox={(value: boolean) => {
						setCheckedCheckbox1(value);
						trackEvent({
							category: 'agreement-check',
							action: 'click-event',
							label: 'Vertragserklärung',
							value: value ? 1 : 0,
						});
					}}
				/>

				<hr className={classes.hr} />

				<AgreementContainer
					data-cy="agreement-check-postbox"
					title={agreementTitle2}
					content={agreementContent2}
					nonContenfulExtraContent={getAgreement2ModalLinks()}
					error={error2}
					buttonMessage={t('kundendaten.keep_reading')}
					checkedCheckbox={checkedCheckbox2}
					setCheckedCheckbox={(value: boolean) => {
						setCheckedCheckbox2(value);
						trackEvent({
							category: 'agreement-check',
							action: 'click-event',
							label: 'Digitales Postfach',
							value: value ? 1 : 0,
						});
					}}
				/>
			</div>

			{!showIPIDModal && !showAllgemeineModal && !showPostboxModal && (
				<NavigationBar onClickContinue={validateAndSubmit} />
			)}

			{/* modals */}
			<DocumentModal
				showModal={showIPIDModal}
				handleCloseFn={() => setShowIPIDModal(false)}
			>
				<DocumentBreakpointWrapper
					mobileDocument={documentToReactComponents(ipidMobile)}
					desktopIframeSrc={ipidDesktop}
					title="IPID"
				/>
			</DocumentModal>

			<DocumentModal
				showModal={showAllgemeineModal}
				handleCloseFn={() => setShowAllgemeineModal(false)}
			>
				<DocumentBreakpointWrapper
					mobileDocument={documentToReactComponents(agbMobile)}
					desktopIframeSrc={agbDesktop}
					title="AGB"
				/>
			</DocumentModal>

			<DocumentModal
				showModal={showPostboxModal}
				handleCloseFn={() => setShowPostboxModal(false)}
			>
				<DocumentBreakpointWrapper
					mobileDocument={postboxMobileDoc}
					desktopIframeSrc={postboxDesktopDoc}
					title="AGB"
				/>
			</DocumentModal>
		</>
	);
};

interface DispatchProps {
	finalizeByEmail(): Promise<void>;
}
const mapStateToProps = (state: AppState) => ({
	contract: state.contract,
});

/* istanbul ignore next */
const mapDispatchToProps = (
	dispatch: ThunkDispatch<Promise<any>, void, Action>
) => ({
	finalizeByEmail: async () => dispatch(finishByEmailProzess()),
});

export default connect( mapStateToProps, mapDispatchToProps )(AgreementKeh);
